// Per-section data fetchers used by the AdminSearch live-results dropdown.
// Each section reuses the same backend endpoint that the corresponding admin
// page already uses, so dropdown previews stay in lock-step with what the
// page would show. Server-side `q` is used where the endpoint supports it;
// for the few that don't, we fetch the list and filter client-side, with a
// short in-memory cache to avoid re-pulling the same data on every keystroke.
//
// Cache strategy (kept small/short by design — admins editing data want to
// see their changes reflected within a couple of seconds):
//  - Server-side `q` endpoints: no cache. The request is already 5 rows so
//    the cost is fine, and BE sorting wins.
//  - Client-side filtered endpoints (Orders / Users / Locations): tiny TTL
//    so a search session uses the same snapshot but a fresh search a few
//    seconds later picks up new data.
//  - `clearLiveSearchCache()` is exported so the AdminSearch component can
//    flush the lot whenever the input regains focus, guaranteeing a fresh
//    snapshot at the start of any new search session.
//
// The dropdown does not deep-link to individual entities — clicking any
// result navigates to the section's page with `?search=Q`, where Q is the
// user's typed query. The page is responsible for reading that param and
// pre-filling its own search input.

import { API_BASE } from "../api/base";
import { apiFetch } from "../api/apiFetch";

export type AdminSectionId =
    | "pages"
    | "orders"
    | "users"
    | "totes"
    | "locations"
    | "products"
    | "inventory"
    | "manifest"
    | "logs";

export interface AdminSearchResult {
    /** Stable identity for React keys. */
    id: string;
    /** Primary text shown in the dropdown row. */
    label: string;
    /** Secondary text shown beneath the label. */
    sublabel?: string;
    /** The query string to put in the destination page's `?search=`
     *  when this row is clicked. Typically the row's most identifying
     *  field — order number for Orders, full name for Users, SKU for
     *  Products, etc. — so the page lands filtered to that exact row,
     *  not the partial query the user happened to have typed. Falls
     *  back to the user's typed query when omitted. */
    searchTerm?: string;
}

export interface AdminSectionConfig {
    /** Section key as used by the dropdown. */
    id: AdminSectionId;
    /** Translation key for the visible label. */
    labelKey: string;
    /** Page route the user lands on when they pick a result. The query is
     *  appended as `?search=...`. */
    route: string;
    /** Fetcher invoked once the query passes the minimum-length gate.
     *  Returns up to 5 rows for the dropdown. */
    fetch: (query: string, warehouse: string) => Promise<AdminSearchResult[]>;
    /** Override the global minimum query length (MIN_LIVE_QUERY_LENGTH)
     *  for this section. Useful for small datasets like Users where
     *  typing one character already narrows enough to be useful. */
    minQueryLength?: number;
}

const ci = (s: string | undefined | null) => (s ?? "").toLowerCase();
const matches = (q: string, ...fields: (string | undefined | null)[]) =>
    fields.some(f => ci(f).includes(q));

// ---- Sorting helpers --------------------------------------------------------
// Recency-first beats relevance-first for admin lookups: when a user is
// searching for "the tote I just made", they want it on top, not buried
// under older alphabetically-similar matches.

const tsOf = (...candidates: (string | undefined | null)[]): number => {
    for (const c of candidates) {
        if (!c) continue;
        const t = Date.parse(c);
        if (!Number.isNaN(t)) return t;
    }
    return 0;
};

const byRecency = <T>(getKeys: (r: T) => (string | undefined | null)[]) =>
    (a: T, b: T) => tsOf(...getKeys(b)) - tsOf(...getKeys(a));

// ---- Cache for client-side filtered endpoints -------------------------------
// Short TTL so admins making changes see them within a couple of seconds.
// The AdminSearch component additionally flushes the cache whenever the
// input regains focus.

interface CacheEntry<T> { at: number; data: T; }
const CLIENT_CACHE_TTL_MS = 5_000;
const caches: Record<string, CacheEntry<unknown>> = {};

const cached = async <T>(key: string, loader: () => Promise<T>): Promise<T> => {
    const hit = caches[key];
    if (hit && Date.now() - hit.at < CLIENT_CACHE_TTL_MS) return hit.data as T;
    const data = await loader();
    caches[key] = { at: Date.now(), data };
    return data;
};

/** Drop every cached snapshot. Called by AdminSearch on input focus so a
 *  new search session always starts from fresh server data. */
export const clearLiveSearchCache = () => {
    for (const k of Object.keys(caches)) delete caches[k];
};

const fetchJson = async <T>(url: string): Promise<T> => {
    // Callers still build full `${API_BASE}/path` strings — strip the
    // base and route through apiFetch so the bearer (when sessionStorage
    // has one) rides on every search query.
    const path = url.startsWith(API_BASE) ? url.slice(API_BASE.length) : url;
    const res = await apiFetch(path);
    if (!res.ok) throw new Error(`${url} → ${res.status}`);
    return res.json();
};

// ----- Section fetchers -------------------------------------------------------
// All return up to 5 rows, sorted most-recent-first where the row carries a
// timestamp the BE exposes. Server-side `q` endpoints over-fetch (page_size
// is bumped above 5) so we have a useful pool to sort within: BE pagination
// would otherwise pin us to whatever order the server picked.

const OVER_FETCH = 50; // pull a page wide enough to sort by recency

const ORDERS_LOOKBACK_DAYS = 30;
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
};

interface OrderRow {
    order_id: string;
    order_number: string;
    customer?: { name?: string };
    items?: { sku?: string; barcode?: string; title?: string }[];
    sales_channel?: string;
    created_at?: string;
    packed_at?: string;
}
const fetchOrders = async (q: string, warehouse: string): Promise<AdminSearchResult[]> => {
    const params = new URLSearchParams({
        warehouse, from: daysAgoISO(ORDERS_LOOKBACK_DAYS), to: todayISO(),
    });
    // /orders returns `{ orders, total }` (NOT a bare array). When the
    // warehouse genuinely has zero orders in the lookback window, the
    // BE may also reply with a non-2xx — treat both shapes as "no
    // matches" rather than an error so the dropdown shows "No results"
    // rather than throwing.
    const data = await cached(
        `orders:${warehouse}`,
        async () => {
            try {
                return await fetchJson<{ orders?: OrderRow[] } | OrderRow[]>(
                    `${API_BASE}/orders?${params}`,
                );
            } catch {
                return [] as OrderRow[];
            }
        },
    );
    const list: OrderRow[] = Array.isArray(data) ? data : (data?.orders ?? []);
    const ql = q.toLowerCase();
    return list
        .filter(o =>
            matches(ql, o.order_number, o.customer?.name)
            || (o.items ?? []).some(it => matches(ql, it.sku, it.barcode, it.title))
        )
        .sort(byRecency<OrderRow>(o => [o.packed_at, o.created_at]))
        .slice(0, 5)
        .map(o => ({
            id: o.order_id,
            label: o.order_number,
            sublabel: [o.customer?.name, o.sales_channel].filter(Boolean).join(" · "),
            searchTerm: o.order_number,
        }));
};

interface UserRow {
    _id: string; UID: number; name: string;
    permissions?: string; warehouse?: string;
    updated_at?: string; created_at?: string;
}
const fetchUsers = async (q: string): Promise<AdminSearchResult[]> => {
    const list = await cached("users", () => fetchJson<UserRow[]>(`${API_BASE}/login/users`));
    const ql = q.toLowerCase();
    return list
        .filter(u => matches(ql, u.name, u.permissions, u.warehouse))
        .sort(byRecency<UserRow>(u => [u.updated_at, u.created_at]))
        .slice(0, 5)
        .map(u => ({
            id: u._id,
            label: u.name,
            sublabel: [u.permissions, u.warehouse].filter(Boolean).join(" · "),
            searchTerm: u.name,
        }));
};

interface ToteRow {
    tote_id: string; status?: string; type?: string; warehouse?: string;
    updated_at?: string; created_at?: string;
}
const fetchTotes = async (q: string): Promise<AdminSearchResult[]> => {
    // No warehouse scope — admin search is global so an admin can find a
    // tote regardless of which site they happen to be signed in to.
    const params = new URLSearchParams({ q, page_size: String(OVER_FETCH) });
    const data = await fetchJson<{ totes?: ToteRow[] } | ToteRow[]>(
        `${API_BASE}/book-in/totes?${params}`,
    );
    const rows = Array.isArray(data) ? data : (data.totes ?? []);
    return rows
        .slice()
        .sort(byRecency<ToteRow>(r => [r.updated_at, r.created_at]))
        .slice(0, 5)
        .map(r => ({
            id: r.tote_id,
            label: r.tote_id,
            sublabel: [r.type, r.status, r.warehouse].filter(Boolean).join(" · "),
            searchTerm: r.tote_id,
        }));
};

interface LocationRow {
    barcode: string; warehouse: string; type: string; zone: string;
    aisle: string; bay: string; level: string; display_name: string;
    created_at?: string;
}
const fetchLocations = async (q: string): Promise<AdminSearchResult[]> => {
    // No warehouse scope — locations are looked up by barcode/display name
    // and admins should be able to find a location across sites.
    const list = await cached("locations:all", () => fetchJson<LocationRow[]>(`${API_BASE}/put-away/locations`));
    const ql = q.toLowerCase();
    return list
        .filter(l => matches(ql, l.barcode, l.display_name, l.zone, l.aisle, l.bay, l.level, l.type, l.warehouse))
        .sort(byRecency<LocationRow>(l => [l.created_at]))
        .slice(0, 5)
        .map(l => ({
            id: `${l.warehouse}:${l.barcode}`,
            label: l.display_name || l.barcode,
            sublabel: [l.barcode, l.warehouse, l.type].filter(Boolean).join(" · "),
            // Barcode is the unique key, so it deep-links most cleanly.
            searchTerm: l.barcode,
        }));
};

interface ProductRow {
    sku: string; title?: string; barcode?: string; supplier_name?: string;
    updated_at?: string; created_at?: string;
}
const fetchProducts = async (q: string): Promise<AdminSearchResult[]> => {
    const params = new URLSearchParams({ q, page_size: String(OVER_FETCH) });
    const data = await fetchJson<{ products?: ProductRow[] } | ProductRow[]>(
        `${API_BASE}/products?${params}`,
    );
    const rows = Array.isArray(data) ? data : (data.products ?? []);
    return rows
        .slice()
        .sort(byRecency<ProductRow>(r => [r.updated_at, r.created_at]))
        .slice(0, 5)
        .map(r => ({
            id: r.sku,
            label: r.title || r.sku,
            sublabel: [r.sku, r.barcode, r.supplier_name].filter(Boolean).join(" · "),
            searchTerm: r.sku,
        }));
};

interface InventoryRow {
    sku: string; warehouse: string; on_hand: number; total: number;
    supplier_name?: string; updated_at?: string;
}
const fetchInventory = async (q: string, warehouse: string): Promise<AdminSearchResult[]> => {
    // Stock is inherently per-warehouse, so we keep the warehouse scope
    // the same way InventoryAdmin defaults to it.
    const params = new URLSearchParams({ q, warehouse, page_size: String(OVER_FETCH) });
    const data = await fetchJson<{ inventory?: InventoryRow[] } | InventoryRow[]>(
        `${API_BASE}/inventory?${params}`,
    );
    const rows = Array.isArray(data) ? data : (data.inventory ?? []);
    return rows
        .slice()
        .sort(byRecency<InventoryRow>(r => [r.updated_at]))
        .slice(0, 5)
        .map(r => ({
            id: `${r.sku}@${r.warehouse}`,
            label: r.sku,
            sublabel: `${r.on_hand} on hand · ${r.warehouse}${r.supplier_name ? " · " + r.supplier_name : ""}`,
            searchTerm: r.sku,
        }));
};

interface ManifestRow {
    manifest_id: number; sku: string; po_number?: string; supplier_name?: string;
    expected_qty: number; received_qty: number; warehouse: string;
    updated_at?: string; created_at?: string;
}
const fetchManifest = async (q: string, warehouse: string): Promise<AdminSearchResult[]> => {
    const params = new URLSearchParams({ q, warehouse, page_size: String(OVER_FETCH) });
    const data = await fetchJson<{ manifests?: ManifestRow[] } | ManifestRow[]>(
        `${API_BASE}/manifest?${params}`,
    );
    const rows = Array.isArray(data) ? data : (data.manifests ?? []);
    return rows
        .slice()
        .sort(byRecency<ManifestRow>(r => [r.updated_at, r.created_at]))
        .slice(0, 5)
        .map(r => ({
            id: String(r.manifest_id),
            label: r.po_number ? `${r.po_number} · ${r.sku}` : r.sku,
            sublabel: [r.supplier_name, `${r.received_qty}/${r.expected_qty}`].filter(Boolean).join(" · "),
            // PO number is the most operationally useful narrowing —
            // SKU often appears under multiple POs.
            searchTerm: r.po_number || r.sku,
        }));
};

interface LogRow {
    id: string; ts: string; user_name?: string; action: string; department: string;
    status: string; tote_id?: string; sku?: string; order_no?: string;
}
const fetchLogs = async (q: string, warehouse: string): Promise<AdminSearchResult[]> => {
    const params = new URLSearchParams({ q, warehouse, page_size: "5" });
    const data = await fetchJson<{ rows?: LogRow[] } | LogRow[]>(
        `${API_BASE}/admin/logs?${params}`,
    );
    // Logs come back sorted ts-desc by the BE already, so we just take 5.
    const rows = Array.isArray(data) ? data : (data.rows ?? []);
    return rows.slice(0, 5).map(r => ({
        id: r.id,
        label: `${r.action}${r.user_name ? " — " + r.user_name : ""}`,
        sublabel: [r.department, r.status, r.sku || r.tote_id || r.order_no].filter(Boolean).join(" · "),
        // Logs don't have a single canonical ID to search by; pick the
        // strongest piece of context the row carries so the page lands
        // narrowed to that entity. Falls back to the action name.
        searchTerm: r.sku || r.tote_id || r.order_no || r.action,
    }));
};

// ----- Public config map -------------------------------------------------------
// `pages` lives in admin-nav.ts and is wired separately by the AdminSearch
// component since it doesn't need a fetcher (synchronous static index).

export const ADMIN_LIVE_SECTIONS: Record<Exclude<AdminSectionId, "pages">, AdminSectionConfig> = {
    orders:    { id: "orders",    labelKey: "orders",            route: "/admin/warehouse/orders",   fetch: fetchOrders },
    // Users is a small set (typically <100 staff per site), so even a
    // single character is a useful narrow — drop the threshold here.
    users:     { id: "users",     labelKey: "users",             route: "/admin/users",              fetch: fetchUsers, minQueryLength: 1 },
    totes:     { id: "totes",     labelKey: "totes_settings",    route: "/admin/warehouse/totes",    fetch: fetchTotes },
    locations: { id: "locations", labelKey: "locations_settings", route: "/admin/warehouse/locations", fetch: fetchLocations },
    products:  { id: "products",  labelKey: "products_settings", route: "/admin/products",           fetch: fetchProducts },
    inventory: { id: "inventory", labelKey: "inventory_title",   route: "/admin/warehouse/inventory", fetch: fetchInventory },
    manifest:  { id: "manifest",  labelKey: "manifest_settings", route: "/admin/warehouse/manifest", fetch: fetchManifest },
    logs:      { id: "logs",      labelKey: "logs",              route: "/admin/logs",               fetch: fetchLogs },
};

export const ADMIN_LIVE_SECTION_ORDER: AdminSectionId[] = [
    "pages", "orders", "users", "totes", "locations", "products", "inventory", "manifest", "logs",
];

export const MIN_LIVE_QUERY_LENGTH = 3;
